import React, { useEffect } from 'react';

import InfoView from 'sharedComponents/InfoView';
import InfoViewSkeleton from 'sharedComponents/InfoViewSkeleton';

import { alphabetizeClients } from 'sharedHelpers/clientHelpers';

import useNavigateToClient from 'sharedHooks/useNavigateToClient';
import useClientListHydration from 'sharedHooks/useClientListHydration';

import { newClientId } from 'sharedHooks/useSaveClient';

import useFilterClients from 'sharedHooks/useFilterClients';
import AddClientButton from './components/AddClientButton';
import ClientListPanelItem from './components/ClientListPanelItem';
import ClientDetailPanel from './components/ClientDetailPanel';

interface ClientsPageProps {
  clientId?: string;
  tabId?: string;
}

function ClientsPage({ clientId, tabId }: ClientsPageProps): JSX.Element {
  const {
    clients,
    loading,
    isBackgroundLoadingClients,
    firstPageResolved,
  } = useClientListHydration();

  const redirectClientId = newClientId();

  const filteredClientsList = useFilterClients({
    clientsList: clients,
    sortFunction: alphabetizeClients,
  });

  const navigateToClient = useNavigateToClient();

  useEffect(() => {
    if (redirectClientId) {
      navigateToClient(redirectClientId);
      newClientId('');
      return;
    }

    if (!clientId && firstPageResolved && filteredClientsList.length) {
      navigateToClient(filteredClientsList[0].id);
    }
  }, [
    clientId,
    redirectClientId,
    firstPageResolved,
    filteredClientsList,
    navigateToClient,
  ]);

  return (
    <>
      {loading && <InfoViewSkeleton />}
      {!loading && (
        <InfoView
          selectedItem={clientId}
          listPanelChildren={({ index, style }) => (
            <ClientListPanelItem
              clientsData={filteredClientsList}
              style={style}
              index={index}
            />
          )}
          detailPanelChildren={
            clientId && (
              <ClientDetailPanel tab={Number(tabId)} clientId={clientId} />
            )
          }
          title="Clients"
          loadingMessage={
            isBackgroundLoadingClients ? 'Loading more clients…' : undefined
          }
          numberOfItems={filteredClientsList.length}
          HeaderButton={<AddClientButton />}
          errorMessage={
            clients.length > filteredClientsList.length
              ? 'There are no clients matching the current filters'
              : 'You have no clients to display'
          }
        />
      )}
    </>
  );
}

ClientsPage.defaultProps = {
  clientId: undefined,
  tabId: '0',
};

export default ClientsPage;
